import type { BalanceDto, SubAccountTransferRequest, TransferDto } from '@api/client';

// The router books the transfer asynchronously, so its outcome is checked after a short pause.
export const OUTCOME_CHECK_DELAY_MS = 1000;

/**
 * Client transfers carry no id, so the outgoing leg is recognised by its target, asset, amount and time.
 */
export function findOutgoingLeg(
  transfers: TransferDto[],
  request: SubAccountTransferRequest,
  submittedAt: number,
): TransferDto | undefined {
  return transfers.find(
    transfer =>
      transfer.direction === 'INTERNAL_WITHDRAW' &&
      transfer.linkedAccountId === request.toAccountId &&
      transfer.asset === request.assetCode &&
      transfer.requestedQuantity === request.quantity &&
      new Date(transfer.createTime).getTime() >= submittedAt - OUTCOME_CHECK_DELAY_MS,
  );
}

export interface MovableBalance extends BalanceDto {
  available: number;
}

/**
 * Mirrors the router's withdrawal checks: at most min(total, free) of the asset, and the account's net equity
 * (usdNotional) must stay non-negative. The client can't see whether negative equity is allowed, so it always applies.
 */
export function availableToMove(
  balance: BalanceDto | undefined,
  usdNotional: number | undefined,
): number {
  if (!balance) {
    return 0;
  }
  const equityInAsset =
    usdNotional != null && balance.assetRefPrice
      ? usdNotional / balance.assetRefPrice
      : Infinity;
  return Math.max(0, Math.min(balance.total, balance.free, equityInAsset));
}
